'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { SECTORES } from '@/data/sectores'
import { ArrowRight, CheckCircle2, X } from 'lucide-react'
import PlayIcon from '@/components/ui/PlayIcon'
import { Coffee, Briefcase, Building2, HeartPulse, GraduationCap, ShoppingBag, Scissors } from 'lucide-react'
import QuickLeadModal from '@/components/ui/QuickLeadModal'
import ComingSoonModal from '@/components/ui/ComingSoonModal'
import SectorMockup from '@/components/ui/SectorMockup'

const ICON_MAP: Record<string, any> = {
  Coffee, Briefcase, Building2, HeartPulse, GraduationCap, ShoppingBag, Scissors,
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' as const, delay },
})

const COLOR_MAP: Record<string, any> = {
  amber: { badge: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'text-amber-500', iconBg: 'bg-amber-50', check: 'text-amber-500' },
  blue: { badge: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'text-blue-500', iconBg: 'bg-blue-50', check: 'text-blue-500' },
  orange: { badge: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'text-orange-500', iconBg: 'bg-orange-50', check: 'text-orange-500' },
  green: { badge: 'bg-green-100 text-green-700 border-green-200', icon: 'text-green-500', iconBg: 'bg-green-50', check: 'text-green-500' },
  violet: { badge: 'bg-violet-100 text-violet-700 border-violet-200', icon: 'text-violet-500', iconBg: 'bg-violet-50', check: 'text-violet-500' },
  pink: { badge: 'bg-pink-100 text-pink-700 border-pink-200', icon: 'text-pink-500', iconBg: 'bg-pink-50', check: 'text-pink-500' },
  rose: { badge: 'bg-rose-100 text-rose-700 border-rose-200', icon: 'text-rose-500', iconBg: 'bg-rose-50', check: 'text-rose-500' },
}

export default function SectorClient({ slug }: { slug: string }) {
  const sector = SECTORES.find((s) => s.slug === slug)
  const [modalOpen, setModalOpen] = useState(false)
  const [demoModalOpen, setDemoModalOpen] = useState(false)

  if (!sector) {
    return <div className="pt-28 pb-20 text-center">Sector no encontrado</div>
  }

  const colors = COLOR_MAP[sector.color] || COLOR_MAP.blue
  const Icon = ICON_MAP[sector.icon] || Briefcase

  return (
    <>
      <QuickLeadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        context={`${sector.nombre} — Tengo claro lo que necesito`}
      />
      <ComingSoonModal
        open={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        productName={sector.nombre}
      />
      <div className="relative z-10 min-h-screen">
        {/* HERO */}
        <section className="pt-28 pb-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <motion.div {...fadeUp(0)}>
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${colors.badge}`}>
                  <Icon className="w-4 h-4" />
                  {sector.nombre}
                </span>
              </motion.div>
              <motion.h1 {...fadeUp(0.08)} className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight tracking-tight">
                {sector.tagline}
              </motion.h1>
              <motion.p {...fadeUp(0.14)} className="text-lg text-slate-500 leading-relaxed max-w-lg">
                {sector.desc}
              </motion.p>
              <motion.div {...fadeUp(0.20)} className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://calendly.com/innovatheratech/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-[15px] shadow-[0_4px_16px_rgba(37,99,235,0.3)] transition-all active:scale-95 bg-blue-600 hover:bg-blue-700"
                >
                  {sector.ctaText}
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-blue-200/80 bg-blue-50/70 text-blue-700 font-semibold text-[15px] hover:bg-blue-100 hover:border-blue-300 transition-all active:scale-95"
                >
                  <PlayIcon className="w-4 h-4 fill-blue-600" />
                  Ir a demo
                </button>
                <button
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-slate-200 bg-white/70 text-slate-700 font-semibold text-[15px] hover:border-slate-300 hover:bg-white transition-all"
                >
                  Tengo claro lo que necesito →
                </button>
              </motion.div>
            </div>

            {/* Mockup panel */}
            <motion.div {...fadeUp(0.15)} className="hidden lg:flex items-center justify-center">
              <SectorMockup
                panelType={sector.panelType}
                color={sector.color}
              />
            </motion.div>
          </div>
        </section>

        {/* PROBLEMAS */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeUp(0)} className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Diagnóstico</span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Lo que frena a tu negocio
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sector.problemas.map((problema, i) => (
                <motion.div key={i} {...fadeUp(i * 0.07)} className="glass-card rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center shrink-0 mt-0.5">
                    <X className="w-4 h-4 text-red-500" />
                  </div>
                  <p className="text-[15px] text-slate-700 font-medium leading-snug">{problema}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SOLUCIONES */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeUp(0)} className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Lo que construimos</span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Lo que construimos para ti
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sector.soluciones.map((solucion, i) => (
                <motion.div key={i} {...fadeUp(i * 0.07)} className="glass-card rounded-2xl p-5 flex items-start gap-4">
                  <div className={`w-8 h-8 rounded-xl ${colors.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <CheckCircle2 className={`w-4 h-4 ${colors.check}`} />
                  </div>
                  <p className="text-[15px] text-slate-700 font-medium leading-snug">{solucion}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div {...fadeUp(0)} className="glass-card rounded-3xl p-10 sm:p-14 shadow-xl space-y-6">
              <div className={`w-14 h-14 rounded-2xl ${colors.iconBg} flex items-center justify-center mx-auto`}>
                <Icon className={`w-7 h-7 ${colors.icon}`} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Empieza hoy
              </h2>
              <p className="text-slate-500 text-lg max-w-md mx-auto leading-relaxed">
                Reserva una llamada gratuita de 30 minutos. Te explicamos exactamente qué construiríamos para tu negocio y cuánto costaría.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://calendly.com/innovatheratech/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-white font-bold text-[15px] shadow-[0_4px_20px_rgba(37,99,235,0.3)] transition-all active:scale-95 bg-blue-600 hover:bg-blue-700"
                >
                  {sector.ctaText}
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-blue-200/80 bg-blue-50/70 text-blue-700 font-semibold text-[15px] hover:bg-blue-100 hover:border-blue-300 transition-all active:scale-95"
                >
                  <PlayIcon className="w-4 h-4 fill-blue-600" />
                  Ir a demo
                </button>
                <button
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-slate-200 bg-white/70 text-slate-700 font-semibold text-[15px] hover:border-slate-300 hover:bg-white transition-all"
                >
                  Tengo claro lo que necesito →
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}
